import React, { useEffect, useState } from "react";
import Button from "../UI/Button";

const FigmaPopup = ({
  trigger,
  setTrigger,
  popupContent,
  nextStep,
  uploadForm,
  setUserData,
  figmaPages,
  setFigmaPages,
  handleFigmaUpload,
}) => {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [imageHeights, setImageHeights] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trigger &&
        !event.target.closest(".app-ai-quote-preview-popup-inner")
      ) {
        setTrigger(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [trigger, setTrigger]);

  const handleSelectedGroup = (group) => {
    const validImages = group.images.filter((img) => img.image);

    if (selectedGroups.includes(group.name)) {
      setSelectedGroups(selectedGroups.filter((g) => g !== group.name));
      setSelectedImages((prevImages) =>
        prevImages.filter(
          (img) => !validImages.some((groupImg) => groupImg.id === img.id)
        )
      );
    } else {
      if (validImages.length > 0) {
        setSelectedGroups([...selectedGroups, group.name]);
        setSelectedImages([...selectedImages, ...validImages]);
      }
    }
  };

  const handleSelectedImage = (image) => {
    if (!selectedImages.some((img) => img.id === image.id)) {
      setSelectedImages([...selectedImages, image]);
    } else {
      setSelectedImages(selectedImages.filter((img) => img.id !== image.id));
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGroups([]);
      setSelectedImages([]);
    } else {
      const validGroups = popupContent.pages
        .filter((page) => page.images.some((img) => img.image))
        .map((page) => page.name);

      const validImages = popupContent.pages
        .flatMap((page) => page.images)
        .filter((img) => img.image);

      setSelectedGroups(validGroups);
      setSelectedImages(validImages);
    }

    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setFigmaPages(selectedImages);
  }, [selectedImages]);

  const fetchImageHeights = (image) => {
    const img = new Image();
    img.src = image.image;

    img.onload = () => {
      setImageHeights((prevHeights) => ({
        ...prevHeights,
        [image.id]: img.height,
      }));
    };
  };

  useEffect(() => {
    popupContent.pages[activeTab]?.images.forEach((image) => {
      if (image.image && !imageHeights[image.id]) {
        fetchImageHeights(image);
      }
    });
  }, [popupContent, activeTab, imageHeights]);

  const sortedImages = [...(popupContent.pages[activeTab]?.images || [])].sort(
    (a, b) => {
      const heightA = imageHeights[a.id] || 0;
      const heightB = imageHeights[b.id] || 0;
      return heightB - heightA;
    }
  );

  return trigger ? (
    <div className="app-ai-quote-preview-popup">
      <div className="app-ai-quote-preview-popup-inner app-ai-quote-preview-popup-inner-light">
        <div className="app-ai-quote-preview-popup-header">
          <div className="app-ai-quote-preview-popup-header-inner">
            <div className="app-ai-quote-preview-popup-checkbox">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              <span />
            </div>
            <h4>Select All</h4>
          </div>
          <button
            onClick={() => setTrigger(false)}
            className="app-ai-quote-preview-popup-close-btn"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_1061_9630)">
                <path
                  d="M18 6L6 18"
                  stroke="#506bf0"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="#506bf0"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1061_9630)">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="app-ai-quote-figma-tabs">
          {popupContent.pages.map((page, index) => (
            <button
              key={index}
              className={`app-ai-quote-figma-tab-button ${
                activeTab === index
                  ? "app-ai-quote-figma-tab-button-active"
                  : ""
              }`}
              onClick={() => setActiveTab(index)}
            >
              {page.name}
            </button>
          ))}
        </div>

        {/* Active Tab Content */}
        <div className="app-ai-quote-figma-popup">
          {popupContent.pages[activeTab] && (
            <div className="app-ai-quote-figma-popup-page">
              <div className="app-ai-quote-figma-popup-page-header">
                <div className="app-ai-quote-figma-popup-page-header-inner">
                  <div className="app-ai-quote-tree-header-select-all">
                    <input
                      type="checkbox"
                      id={`page.name-${activeTab}`}
                      name={popupContent.pages[activeTab].name}
                      checked={selectedGroups.includes(
                        popupContent.pages[activeTab].name
                      )}
                      onChange={() =>
                        handleSelectedGroup(popupContent.pages[activeTab])
                      }
                    />
                    <label htmlFor={`page.name-${activeTab}`}>
                      {popupContent.pages[activeTab].name}
                    </label>
                  </div>
                </div>
              </div>

              <div className="app-ai-quote-figma-popup-frames">
                {sortedImages.map(
                  (image) =>
                    image.image && (
                      <div
                        key={image.id}
                        className={`app-ai-quote-figma-popup-frame ${
                          selectedImages.some((img) => img.id === image.id)
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => handleSelectedImage(image)}
                      >
                        <img src={image.image} alt={image.name} />
                        <h4>{image.name}</h4>
                      </div>
                    )
                )}
              </div>
            </div>
          )}
        </div>

        <div className="app-ai-quote-preview-popup-footer">
          <Button onClick={handleFigmaUpload}>Calculate Now</Button>
        </div>
      </div>
    </div>
  ) : null;
};

export default FigmaPopup;
